const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000";

type TokenGetter = () => string | null;
type TokenRefresher = () => Promise<string | null>;

let getToken: TokenGetter = () => null;
let refreshToken: TokenRefresher = async () => null;

export function configureAuth(getter: TokenGetter, refresher: TokenRefresher) {
  getToken = getter;
  refreshToken = refresher;
}

export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
  _retried = false,
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: "include",
  });

  if (res.status === 401 && !_retried) {
    const newToken = await refreshToken();
    if (newToken) {
      return apiFetch<T>(path, options, true);
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, messaggioErrore(body.detail, res.statusText), body.detail);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

/** Un errore di validazione così come lo restituisce FastAPI. */
type ErroreDiCampo = { loc?: (string | number)[]; msg?: string; type?: string };

/**
 * Riduce il `detail` di FastAPI a una frase leggibile.
 *
 * Quando la validazione fallisce, `detail` non è una stringa ma un **elenco di
 * oggetti**, uno per campo sbagliato. Passandolo così com'era al costruttore di
 * `Error`, JavaScript lo convertiva in testo nell'unico modo che conosce: chi
 * sbagliava la password si vedeva rispondere «[object Object]».
 *
 * Un errore che non dice cosa è andato storto è peggio di nessun errore: chi lo
 * legge riprova a caso.
 */
export function messaggioErrore(detail: unknown, ripiego = "Errore"): string {
  if (typeof detail === "string" && detail.trim()) return detail;

  if (Array.isArray(detail)) {
    const frasi = (detail as ErroreDiCampo[])
      .map((e) => (typeof e?.msg === "string" ? e.msg : ""))
      // Pydantic antepone «Value error, » ai messaggi scritti a mano: è rumore
      // per chi legge.
      .map((m) => m.replace(/^Value error,\s*/i, "").trim())
      .filter(Boolean);
    if (frasi.length) return [...new Set(frasi)].join(" · ");
  }

  if (detail && typeof detail === "object") {
    const m = (detail as { msg?: unknown; message?: unknown });
    if (typeof m.msg === "string") return m.msg;
    if (typeof m.message === "string") return m.message;
  }

  return ripiego;
}

/** Vero se fra gli errori di validazione ce n'è uno sul campo indicato. */
export function erroreSulCampo(detail: unknown, campo: string): boolean {
  if (!Array.isArray(detail)) return false;
  return (detail as ErroreDiCampo[]).some((e) =>
    Array.isArray(e?.loc) && e.loc.some((p) => p === campo),
  );
}

export class ApiError extends Error {
  status: number;
  /** Il `detail` originale: serve a capire QUALE campo è stato rifiutato. */
  detail: unknown;

  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}
